import { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import axios from 'axios'
import * as Sentry from '@sentry/react'
import { useAuth } from '../context/AuthContext'
import { useToast } from '../context/ToastContext'
import { supabase } from '../lib/supabase'
import SEO from '../components/SEO'
import Turnstile from '../components/Turnstile'
import MusicNoteAnimation from '../components/MusicNoteAnimation'

// Allowlist of response-header names safe to attach to a signup-error
// capture. Anything not in this set is dropped so PII / auth material
// can never reach Sentry via err.headers.
const SAFE_HEADER_KEYS = ['content-type', 'x-request-id', 'retry-after', 'cf-ray']

function extractSafeHeaders(headers) {
  if (!headers) return undefined
  const out = {}
  try {
    for (const key of SAFE_HEADER_KEYS) {
      const v = typeof headers.get === 'function' ? headers.get(key) : headers[key]
      if (v != null) out[key] = v
    }
  } catch {
    return undefined
  }
  return Object.keys(out).length ? out : undefined
}

const PASSWORD_RULES = [
  { label: 'At least 8 characters',  test: (p) => p.length >= 8 },
  { label: 'One uppercase letter',    test: (p) => /[A-Z]/.test(p) },
  { label: 'One number',              test: (p) => /\d/.test(p) },
  { label: 'One special character',   test: (p) => /[^A-Za-z0-9]/.test(p) },
]

function mapSignupError(error) {
  if (!error) return null
  if (error.code === 'weak_password') {
    const reasons = error.weakPassword?.reasons ?? []
    if (reasons.includes('pwned')) {
      return { field: 'password', message: 'This password has appeared in a known data breach. Please choose a different one.' }
    }
    if (reasons.includes('characters')) {
      return { field: 'password', message: 'Password must include a mix of character types (lowercase, uppercase, digits, symbols).' }
    }
    if (reasons.includes('length')) {
      return { field: 'password', message: 'Password is too short. Please use a longer password.' }
    }
    return { field: 'password', message: 'Password is too weak. Try a longer or more varied password.' }
  }
  if (error.code === 'user_already_registered' || error.code === 'email_exists') {
    return { field: 'email', message: 'An account with this email already exists. Try signing in.' }
  }
  if (error.code === 'email_address_invalid') {
    return { field: 'email', message: 'Please enter a valid email address.' }
  }
  if (error.code === 'over_email_send_rate_limit' || error.status === 429) {
    return { field: 'email', message: 'Too many signups recently. Please try again in a few minutes.' }
  }
  if (error.code === 'signup_disabled') {
    return { field: 'email', message: 'Signups are temporarily disabled. Please try again later.' }
  }
  return null
}

export default function Signup() {
  const navigate = useNavigate()
  const { signUp } = useAuth()
  const toast = useToast()

  const [checking, setChecking] = useState(true)

  useEffect(() => {
    axios.get('/api/user/signup-check')
      .then(({ data }) => {
        if (!data.open) navigate('/at-capacity', { replace: true })
        else setChecking(false)
      })
      .catch(() => setChecking(false))
  }, [navigate])

  const [form, setForm] = useState({
    username: '', email: '', password: '', confirm: '', agreed: false,
  })
  const [errors, setErrors]           = useState({})
  const [loading, setLoading]         = useState(false)
  const [step, setStep]               = useState(1)
  const [captchaToken, setCaptchaToken] = useState(null)
  // Honeypot — real users never fill this; bots that auto-fill all fields will.
  const [honeypot, setHoneypot] = useState('')

  function handleChange(e) {
    const { name, value, type, checked } = e.target
    setForm((f) => ({ ...f, [name]: type === 'checkbox' ? checked : value }))
    setErrors((e) => ({ ...e, [name]: undefined }))
  }

  function validate() {
    const errs = {}
    const u = form.username.trim().toLowerCase()
    if (!u) errs.username = 'Username is required.'
    else if (!/^[a-z0-9_]{2,30}$/.test(u)) errs.username = 'Username must be 2–30 characters: letters, numbers, and underscores.'
    else if (u.startsWith('verse_')) errs.username = 'Usernames cannot start with “verse_”.'

    if (!form.email) errs.email = 'Email is required.'
    if (!form.password) errs.password = 'Password is required.'
    else if (!PASSWORD_RULES.every(r => r.test(form.password))) errs.password = 'Password does not meet all requirements.'
    if (form.password !== form.confirm) errs.confirm = 'Passwords do not match.'
    if (!form.agreed) errs.agreed = 'You must accept the terms.'
    return errs
  }

  async function handleSubmit(e) {
    e.preventDefault()
    // Honeypot trap: silently abort if filled. Don't show an error — let the bot
    // think the form was submitted successfully so they don't retry.
    if (honeypot) return
    const errs = validate()
    if (Object.keys(errs).length) { setErrors(errs); return }
    if (import.meta.env.VITE_TURNSTILE_SITE_KEY && !captchaToken) {
      setErrors({ captcha: 'Please complete the verification.' })
      return
    }

    // Server-side disposable email check before hitting Supabase Auth.
    // Why: stops throwaway addresses at the gate so they never become accounts.
    try {
      const { data } = await axios.get(`/api/user/signup-check?email=${encodeURIComponent(form.email)}`)
      if (data && data.emailValid === false) {
        setErrors({ email: data.emailError ?? 'This email address is not allowed.' })
        return
      }
    } catch {
      // If the check itself fails, fall through — Supabase Auth will still
      // validate the address format and the rate limiter still applies.
    }

    setLoading(true)
    const cleanUsername = form.username.trim().toLowerCase()
    const { data, error } = await signUp(form.email, form.password, cleanUsername, null, undefined, captchaToken)
    if (error) {
      // Capture the raw error to Sentry BEFORE any transformation so the next
      // reported signup failure has diagnostic content. Allowlist fields
      // explicitly — never pass the raw error as a single extras blob.
      try {
        Sentry.captureException(error, {
          tags: { surface: 'signup' },
          extra: {
            error_message: error.message,
            error_name: error.name,
            error_code: error.code,
            error_status: error.status,
            error_headers: extractSafeHeaders(error.headers),
          },
        })
      } catch {
        // Observability must never regress the signup path.
      }
      // Map known Supabase Auth error codes to actionable copy; fall through
      // to the existing substring-based field routing for unmapped codes.
      const mapped = mapSignupError(error)
      if (mapped) {
        setErrors({ [mapped.field]: mapped.message })
      } else {
        const msg = error.message.toLowerCase()
        const field = msg.includes('password') ? 'password'
                    : msg.includes('email')    ? 'email'
                    : msg.includes('user')     ? 'username'
                    : 'email'
        setErrors({ [field]: error.message })
      }
      setCaptchaToken(null)
      setLoading(false)
      return
    }

    // Defer the dob + username writes until the user clicks the verify-email
    // link and a session is established. AuthContext's INITIAL_SESSION
    // handler drains this key and fires POST /dob-submit + PATCH /username.
    // Cross-tab verify clicks
    // leave the key empty in the verifying tab — DobGate re-prompts for dob;
    // username + display_name remain correct because username is still
    // passed to handle_new_user via signUp metadata.
    if (data?.user?.id) {
      try {
        sessionStorage.setItem('verse_signup_pending', JSON.stringify({
          username: cleanUsername,
        }))
      } catch (err) {
        console.warn('[signup] could not persist signup pending state:', err.message)
      }
    }

    setStep(2)
  }

  if (checking) return null

  if (step === 2) return <SuccessScreen email={form.email} />

  const pwStrength = PASSWORD_RULES.filter((r) => r.test(form.password)).length

  return (
    <div className="auth-page">
      <SEO title="Sign Up" description="Create your free Verse account and start writing today." />
      {/* ── Left panel ── */}
      <div className="auth-panel auth-panel--signup">
        <div className="auth-panel__blobs">
          <div className="auth-panel__blob auth-panel__blob--1" />
          <div className="auth-panel__blob auth-panel__blob--2" />
          <div className="auth-panel__blob auth-panel__blob--3" />
        </div>

        <div className="auth-panel__content">
          <Link to="/" className="auth-panel__logo">
            <img
              src="/verse-icon.png"
              alt="Verse"
              className="auth-panel__logo-icon footer__logo-icon"
              style={{ background: '#f5b0c8' }}
            />
            Verse
          </Link>

          <h2 className="auth-panel__heading">Join a world of stories</h2>
          <p className="auth-panel__subheading">
            Thousands of writers are already sharing their worlds here. Your story is the next great adventure.
          </p>

        </div>
      </div>

      {/* ── Right: form ── */}
      <div className="auth-form-panel">
        <div className="auth-form-wrap">
          <div className="auth-form-header">
            <h1 className="auth-form-title">Create your account</h1>
            <p className="auth-form-subtitle">It's free, forever.</p>
          </div>

          <p className="auth-form-subtitle" style={{ textAlign: 'center', marginBottom: 16 }}>You must be 13 or older to use Verse.</p>

          <div className="auth-social-btns">
            <button
              className="auth-social-btn"
              onClick={async () => {
                try { sessionStorage.setItem('verse_signin_intent', '1') } catch {}
                const { error } = await supabase.auth.signInWithOAuth({ provider: 'google', options: { redirectTo: window.location.origin } })
                if (error) {
                  try { sessionStorage.removeItem('verse_signin_intent') } catch {}
                  toast(error.message, 'error')
                }
              }}
            >
              <GoogleIcon /> Continue with Google
            </button>
          </div>

          <div className="auth-divider"><span>or sign up with email</span></div>

          <form className="auth-form" onSubmit={handleSubmit} noValidate>
            {/* Honeypot — hidden from real users; bots that auto-fill all
                fields will trip this and the submission silently aborts. */}
            <input
              type="text"
              name="website"
              tabIndex={-1}
              autoComplete="off"
              value={honeypot}
              onChange={(e) => setHoneypot(e.target.value)}
              style={{ position: 'absolute', left: '-9999px', width: 1, height: 1, opacity: 0 }}
              aria-hidden="true"
            />
            {/* Username */}
            <div className="auth-field">
              <label className="auth-label" htmlFor="username">Username</label>
              <input
                id="username" name="username" type="text"
                className={`auth-input${errors.username ? ' auth-input--error' : ''}`}
                placeholder="your_pen_name"
                value={form.username}
                onChange={handleChange}
                autoComplete="username"
              />
              {errors.username && <span className="auth-field-error">{errors.username}</span>}
            </div>

            {/* Email */}
            <div className="auth-field">
              <label className="auth-label" htmlFor="email">Email</label>
              <input
                id="email" name="email" type="email"
                className={`auth-input${errors.email ? ' auth-input--error' : ''}`}
                placeholder="you@example.com"
                value={form.email}
                onChange={handleChange}
                autoComplete="email"
              />
              {errors.email && <span className="auth-field-error">{errors.email}</span>}
            </div>

            {/* Password */}
            <div className="auth-field">
              <label className="auth-label" htmlFor="password">Password</label>
              <input
                id="password" name="password" type="password"
                className={`auth-input${errors.password ? ' auth-input--error' : ''}`}
                placeholder="••••••••"
                value={form.password}
                onChange={handleChange}
                autoComplete="new-password"
              />
              {errors.password && <span className="auth-field-error">{errors.password}</span>}
              {form.password && (
                <div className="pw-strength">
                  <div className="pw-strength__bars">
                    {[0, 1, 2, 3].map((i) => (
                      <div key={i} className="pw-strength__bar" style={{
                        background: i < pwStrength
                          ? pwStrength <= 1 ? '#EF4444' : pwStrength === 2 ? '#F59E0B' : pwStrength === 3 ? '#F59E0B' : '#10B981'
                          : 'var(--border)',
                      }} />
                    ))}
                  </div>
                  <div className="pw-strength__rules">
                    {PASSWORD_RULES.map((rule) => (
                      <span key={rule.label} className={`pw-rule${rule.test(form.password) ? ' pw-rule--met' : ''}`}>
                        {rule.test(form.password) ? '✓' : '○'} {rule.label}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Confirm password */}
            <div className="auth-field">
              <label className="auth-label" htmlFor="confirm">Confirm password</label>
              <input
                id="confirm" name="confirm" type="password"
                className={`auth-input${errors.confirm ? ' auth-input--error' : ''}`}
                placeholder="••••••••"
                value={form.confirm}
                onChange={handleChange}
                autoComplete="new-password"
              />
              {errors.confirm && <span className="auth-field-error">{errors.confirm}</span>}
            </div>

            <div className={`auth-checkbox-row${errors.agreed ? ' auth-checkbox-row--error' : ''}`}>
              <input id="agreed" name="agreed" type="checkbox"
                className="auth-checkbox" checked={form.agreed} onChange={handleChange} />
              <label htmlFor="agreed" className="auth-checkbox-label">
                I agree to the <a href="/terms" className="auth-switch-link">Terms of Service</a>
                {' '}and <a href="/privacy" className="auth-switch-link">Privacy Policy</a>
              </label>
            </div>
            {errors.agreed && <span className="auth-field-error" style={{ marginTop: -8 }}>{errors.agreed}</span>}

            <Turnstile onToken={setCaptchaToken} onExpire={() => setCaptchaToken(null)} />
            {errors.captcha && <span className="auth-field-error">{errors.captcha}</span>}

            <button type="submit" className="btn btn-primary auth-submit" disabled={loading}>
              {loading ? <Spinner /> : 'Create account'}
            </button>
          </form>

          <p className="auth-switch">
            Already have an account?{' '}
            <Link to="/login" className="auth-switch-link">Sign in</Link>
          </p>
        </div>
      </div>
    </div>
  )
}

function SuccessScreen({ email }) {
  const [sending, setSending] = useState(false)
  const [sentAt, setSentAt] = useState(null)
  const [err, setErr] = useState(null)

  async function resend() {
    setSending(true); setErr(null)
    try {
      // Pre-session path: resend directly via Supabase since the user has no
      // token yet. The server endpoint is only callable once they log in.
      const { error } = await supabase.auth.resend({ type: 'signup', email })
      if (error) throw error
      setSentAt(Date.now())
    } catch (e) {
      setErr(e.message ?? 'Could not resend. Try again shortly.')
    } finally {
      setSending(false)
    }
  }

  const cooldownLeft = sentAt ? Math.max(0, 60 - Math.floor((Date.now() - sentAt) / 1000)) : 0

  return (
    <div className="auth-success">
      <MusicNoteAnimation />
      <div className="auth-success__card">
        <div className="auth-success__icon">✉️</div>
        <h2 className="auth-success__title">Check your inbox!</h2>
        <p className="auth-success__desc">
          We sent a confirmation link to <strong>{email}</strong>.
          Click it to activate your account and start writing.
        </p>
        {sentAt && !err && <p style={{ color: 'var(--green)', fontSize: 13 }}>Sent again. Check your inbox.</p>}
        {err && <p style={{ color: 'var(--red)', fontSize: 13 }}>{err}</p>}
        <div style={{ display: 'flex', gap: 12, marginTop: 8 }}>
          <button
            className="btn btn-outline"
            onClick={resend}
            disabled={sending || cooldownLeft > 0}
          >
            {sending ? 'Sending…' : cooldownLeft > 0 ? `Resend in ${cooldownLeft}s` : "Didn't get it? Resend"}
          </button>
          <Link to="/" className="btn btn-primary">Back to Verse</Link>
        </div>
      </div>
    </div>
  )
}

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
      <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" fill="#4285F4"/>
      <path d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z" fill="#34A853"/>
      <path d="M3.964 10.706A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.706V4.962H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.038l3.007-2.332z" fill="#FBBC05"/>
      <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.962L3.964 7.294C4.672 5.163 6.656 3.58 9 3.58z" fill="#EA4335"/>
    </svg>
  )
}

function Spinner() {
  return (
    <span className="auth-spinner" aria-label="Loading">
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
        <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" strokeLinecap="round"/>
      </svg>
    </span>
  )
}
